// FAQ content as data, kept apart from the page components so the
// router can build FAQ structured data without pulling every info
// page into the initial download.

export const FAQ_SECTIONS = [
    {
      id: "how-it-works",
      title: "How it works",
      items: [
        {
          q: "What exactly are you sending?",
          a: "One 4×6 photo postcard. The customer preview shows the CareCard-owned photo/message artwork; final production stock, postage treatment, and provider-rendered zones remain subject to the launch proof gates.",
        },
        {
          q: "How long does delivery take?",
          a: "Timing varies after an order is eligible for production. Tracking distinguishes confirmed events, provider/postal estimates, facility acceptance, and recipient receipt; it never guarantees facility acceptance or recipient receipt.",
        },
        {
          q: "Can I schedule a card for a specific day?",
          a: "Yes — you can schedule up to 6 days out. The card holds in PAID state and releases to print at midnight on the scheduled date (America/New_York). You can cancel it any time before it starts printing, including on the send date itself. There is no edit step: to change anything, cancel and place a new order.",
        },
        {
          q: "Will the recipient see who sent it?",
          a: "Yes: your return address prints on the card, the way return addresses work on ordinary mail, and your name and message are on the card as well. Every postcard needs a return address — it is required to send a card. We hand the return address to the mail provider for that one card and do not keep it afterwards. Rejected mail may be returned, held, or disposed of depending on facility and postal handling.",
        },
      ],
    },
    {
      id: "what-you-can-send",
      title: "What you can and can't send",
      items: [
        {
          q: "What images are not allowed?",
          a: "Per BSO mail policy: no nudity or sexually suggestive content, no gang colors or insignia, no weapons (real or implied), no contraband (drugs, paraphernalia), no maps or escape diagrams, no images of correctional facilities. Every photo is reviewed by a person before printing.",
        },
        {
          q: "What text is not allowed?",
          a: "No threats, no profanity directed at staff, no coded references to contraband, no information that could facilitate escape (yard schedules, transport routes, etc.), no third-party communication relays. Text is screened automatically and may be flagged for human review.",
        },
        {
          q: "What happens if my card is rejected?",
          a: "If moderation rejects your image or text before printing, we cancel the card, record the $3.99 as owed back to you, and email you the category that triggered the rejection. Refunds are processed by hand, so allow a few business days. You can fix the issue and submit a new order at no penalty.",
        },
      ],
    },
    {
      id: "bso-compliance",
      title: "Sheriff's office compliance",
      bso: true,
      items: [
        {
          q: "Is CareCard Express run by the BSO or the St. Lucie Sheriff?",
          a: "No. CareCard Express is an independent service, not affiliated with the Broward Sheriff's Office, the St. Lucie County Sheriff's Office, or Smart Communications. We mail postcards via USPS the same way you would if you printed and stamped them yourself. Neither sheriff endorses, approves, or shares revenue with us.",
        },
        {
          q: "Why does the recipient need an arrest number?",
          a: "Mailrooms route mail by number, not by name. Multiple people can share a name; without the number, your card may be returned. For Broward (BSO), use the arrest/booking number from the BSO arrest search. For St. Lucie County Jail, use their St. Lucie jail ID number (numbers only, usually 4 to 6 digits) from the St. Lucie Sheriff custody lookup. If the lookup shows a release date, the card can't be delivered.",
        },
        {
          q: "What if the recipient is transferred mid-transit?",
          a: "A transfer can make an address stale. CareCard does not promise that it can detect a USPS return or facility transfer automatically. If you learn the recipient moved, contact support with your order ID; any cancellation or refund is recorded only from the actual order/payment state.",
        },
      ],
    },
    {
      id: "pricing-and-taxes",
      title: "Pricing & taxes",
      items: [
        {
          q: "Is $3.99 really the total?",
          a: "$3.99 flat, with no hidden fees. Printing and postage are included. Printing/mailing can proceed only after the separate review and release gates pass; recipient receipt is not a tracked guarantee.",
        },
        {
          q: "Do you offer bulk pricing?",
          a: "Not at v1. We deliberately limited the service to one card per order to keep moderation, fraud prevention, and customer support simple. A multi-card flow is on the roadmap.",
        },
      ],
    },
    {
      id: "refunds",
      title: "Refunds & returns",
      items: [
        {
          q: "When do you owe me a refund?",
          a: "Three cases: (1) moderation rejects your image or text before print, (2) you cancel while the card is still cancellable, (3) you ask us to delete your data and the card had not been printed yet. In all three we cancel the card and record the $3.99 as owed to you. None of it is automatic — a person pays it back, so allow a few business days.",
        },
        {
          q: "What if I just changed my mind?",
          a: "You can cancel while the card reads PAID, IN_REVIEW, or RENDERING — that is, right up until it goes to print. Hit Cancel from your order page or email support@carecardexpress.com with your order ID. We record the full $3.99 back to you, no questions.",
        },
        {
          q: "How long does a refund take to land?",
          // Was "Stripe processes the refund instantly on our side; it typically
          // appears on your statement in 5–10 business days". No processor is
          // wired, so nothing is processed instantly or otherwise.
          a: "We record the refund you're owed the moment your card is cancelled. Refunds are handled by hand at present rather than automatically, so email support@carecardexpress.com with your order ID if you haven't heard from us. We never charge a restocking or processing fee.",
        },
      ],
    },
    {
      id: "privacy",
      title: "Privacy & data deletion",
      items: [
        {
          q: "Do you sell my data?",
          // Previously listed five processors as if all five received data.
          // Only the ones actually wired belong in a list a customer reads as
          // a disclosure.
          a: "No. We never sell, share for advertising, or use customer data to train ML models. We share only what's strictly required, and only with processors that are actually connected: PostGrid (printing and postage), Stripe (payments) and Postmark (email). No automated image-moderation vendor receives your photo — a person on our team reviews it. Our Privacy Policy lists every processor we may add.",
        },
        {
          q: "How long do you keep my data?",
          a: "Your photo is destroyed 90 days after the card is delivered or cancelled. Your email, the recipient's name, their arrest number and your message are removed 90 days after that same point. What stays is the order record with its amounts and dates, which by then has nobody's name attached to it.",
        },
        {
          q: "How do I delete my data sooner?",
          a: <>Visit <a className="ip-link" href="#privacy/deletion-request">/privacy-deletion</a> and enter your email. We send a confirmation link; clicking it erases everything we hold on you straight away, not on a queue. If a card hasn't been printed yet we cancel it and record the $3.99 as owed back to you. A card already in the mail can't be recalled. This is irreversible. Your order ID still resolves afterwards, but it will no longer show the recipient, your message, or the photo.</>,
        },
      ],
    },
];

// WEB-04 §15.4: the named help-topic routes. Each topic's content lives on
// one canonical page (the same page the /help index links to), so the topic
// routes resolve there rather than maintaining a second, contradictory copy.
export const HELP_TOPIC_REDIRECTS = {
  "order-not-received": "/track",
  "wrong-address": "/facility-rules",
  "payment-problem": "/faq",
  "postcard-rejected": "/content-policy",
  "wrong-recipient": "/contact",
  "change-or-cancel-order": "/cancellation-policy",
  "refunds": "/refund-policy",
  "photo-requirements": "/content-policy",
  "message-requirements": "/content-policy",
  "facility-rules": "/facility-rules",
  "account": "/account",
  "contact": "/contact",
};
